import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import ContactNotesDialogListItem from '../ContactNotesDialogListItem';
import ConfirmationModal from '../ConfirmationModal';
import ContactNotesEditModal from '../ContactNotesEditModal';

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import ButtonWithSpinner from '../ButtonWithSpinner';

import useTicketNotes from '../../hooks/useTicketNotes';
import { getTokens } from '../../styles/tokens';

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minWidth: 0,
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 6,
        },
        cancel: {
            fontWeight: 600,
            color: theme.palette.text.secondary,
        },
        save: {
            position: 'relative', // âncora do spinner do ButtonWithSpinner
            fontWeight: 600,
            minWidth: 88,
            borderRadius: 16,
            boxShadow: 'none',
        },
        list: {
            width: '100%',
            maxHeight: 220,
            margin: '4px 0 0',
            padding: '8px 0 0',
            borderTop: `1px solid ${t.line}`,
            overflow: 'auto',
            ...theme.scrollbarStyles,
        },
    };
});

const NoteSchema = Yup.object().shape({
    note: Yup.string()
        .min(2, "Too Short!")
        .required("Required")
});
export function ContactNotes({ ticket }) {
    const { id: ticketId, contactId } = ticket
    const classes = useStyles()
    const [newNote, setNewNote] = useState({ note: "" });
    const [loading, setLoading] = useState(false)
    const [showOnDeleteDialog, setShowOnDeleteDialog] = useState(false)
    const [selectedNote, setSelectedNote] = useState({})
    const [notes, setNotes] = useState([])
    const { saveNote, deleteNote, listNotes } = useTicketNotes()
    const [editingNote, setEditingNote] = useState(null);

    useEffect(() => {
        async function openAndFetchData() {
            handleResetState()
            await loadNotes()
        }
        openAndFetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleResetState = () => {
        setNewNote({ note: "" })
        setLoading(false)
    }

    const handleChangeComment = (e) => {
        setNewNote({ note: e.target.value })
    }

    const handleEdit = (note) => {
        console.log(note)
        setEditingNote(note);
    };

    const handleSave = async values => {
        setLoading(true)
        try {
            await saveNote({
                ...values,
                ticketId,
                contactId
            })
            await loadNotes()
            setNewNote({ note: '' })
            toast.success('Observação adicionada com sucesso!')
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    const handleOpenDialogDelete = (item) => {
        setSelectedNote(item)
        setShowOnDeleteDialog(true)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await deleteNote(selectedNote.id)
            await loadNotes()
            setSelectedNote({})
            toast.success('Observação excluída com sucesso!')
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    const loadNotes = async () => {
        setLoading(true)
        try {
            const notes = await listNotes({ ticketId, contactId })
            setNotes(notes)
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }


    const renderNoteList = () => {
        return notes.map((note) => {
            return <ContactNotesDialogListItem
                note={note}
                key={note.id}
                deleteItem={handleOpenDialogDelete}
                editItem={() => handleEdit(note)}
            />
        })
    }

    return (
        <>
            <ContactNotesEditModal
                open={editingNote !== null}
                onClose={() => setEditingNote(null)}
                note={editingNote ? editingNote.note : ''}
                onSave={handleSave}
            />
            <ConfirmationModal
                title="Excluir Registro"
                open={showOnDeleteDialog}
                onClose={setShowOnDeleteDialog}
                onConfirm={handleDelete}
            >
                Deseja realmente excluir este registro?
            </ConfirmationModal>
            <Formik
                initialValues={newNote}
                enableReinitialize={true}
                validationSchema={NoteSchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSave(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >

                {({ touched, errors, setErrors }) => (
                    <Form className={classes.form}>
                        <Field
                            as={TextField}
                            name="note"
                            rows={3}
                            placeholder={i18n.t("ticketOptionsMenu.appointmentsModal.placeholder")}
                            multiline={true}
                            error={touched.note && Boolean(errors.note)}
                            helperText={touched.note && errors.note}
                            variant="outlined"
                            onChange={handleChangeComment}
                            fullWidth
                            inputProps={{ 'aria-label': i18n.t("ticketOptionsMenu.appointmentsModal.textarea") }}
                        />
                        <div className={classes.actions}>
                            <Button
                                onClick={() => {
                                    setNewNote("");
                                    setErrors({});
                                }}
                                size="small"
                                className={classes.cancel}
                            >
                                Cancelar
                            </Button>
                            <ButtonWithSpinner
                                loading={loading}
                                color="primary"
                                type="submit"
                                variant="contained"
                                size="small"
                                className={classes.save}
                            >
                                Salvar
                            </ButtonWithSpinner>
                        </div>
                        {notes.length > 0 && (
                            <List className={classes.list}>
                                {renderNoteList()}
                            </List>
                        )}
                    </Form>
                )}
            </Formik>
        </>
    );
}